import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { API } from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import BackButton from "@/components/BackButton";
import { Image as ImageIcon, Film, Play, Folder, ArrowLeft } from "lucide-react";

export default function Gallery() {
  const [folders, setFolders] = useState([]);
  const [current, setCurrent] = useState(null);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const { tick } = useActivity();

  useEffect(() => {
    api.get("/gallery/folders").then((r) => setFolders(r.data)).catch(() => {});
  }, [tick]);

  const openFolder = (folder) => {
    setCurrent(folder);
    api.get(`/gallery?folder_id=${folder.id}`).then((r) => setItems(r.data)).catch(() => {});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
      <BackButton className="mb-6" />
      <span className="text-xs font-bold tracking-[0.3em] uppercase text-cyan-400">Media</span>
      <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-black tracking-tighter uppercase text-zinc-50">
        Galleria
      </h1>

      {!current ? (
        <>
          <p className="mt-3 text-zinc-400">Sfoglia gli album di foto e video.</p>
          {folders.length === 0 ? (
            <p className="mt-16 text-center text-zinc-500" data-testid="empty-folders">
              Nessun album disponibile.
            </p>
          ) : (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {folders.map((f, i) => (
                <motion.button
                  key={f.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => openFolder(f)}
                  data-testid={`folder-${f.id}`}
                  className="group relative aspect-[4/3] overflow-hidden border border-zinc-800 bg-zinc-900 text-left hover:border-yellow-400/50 transition-colors duration-200"
                >
                  {f.cover_id ? (
                    f.cover_type === "video" ? (
                      <video src={`${API}/gallery/file/${f.cover_id}`} muted className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-300" />
                    ) : (
                      <img src={`${API}/gallery/file/${f.cover_id}`} alt={f.name} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
                    )
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
                  <div className="relative z-10 h-full flex flex-col justify-end p-5">
                    <Folder className="h-7 w-7 text-yellow-400 mb-2" />
                    <h3 className="font-heading text-lg font-bold text-zinc-50 truncate">{f.name}</h3>
                    <span className="text-xs text-zinc-400">
                      <span className="font-display text-cyan-400">{f.count}</span> element{f.count === 1 ? "o" : "i"}
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <button onClick={() => setCurrent(null)} data-testid="back-to-folders"
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-zinc-400 hover:text-yellow-400 transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Album
          </button>
          <h2 className="mt-3 font-heading text-2xl font-bold uppercase text-zinc-50">{current.name}</h2>
          {items.length === 0 ? (
            <p className="mt-16 text-center text-zinc-500" data-testid="empty-gallery">Album vuoto.</p>
          ) : (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.filter((it) => it.status !== "processing" && it.status !== "failed").map((it, i) => (
                <motion.button
                  key={it.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => setActive(it)}
                  data-testid={`gallery-item-${it.id}`}
                  className="group relative aspect-square overflow-hidden border border-zinc-800 bg-zinc-900"
                >
                  {it.media_type === "video" ? (
                    <>
                      <video src={`${API}/gallery/file/${it.id}`} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-300" muted />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="h-12 w-12 rounded-full bg-yellow-400/90 flex items-center justify-center">
                          <Play className="h-5 w-5 text-zinc-950 ml-0.5" fill="currentColor" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={`${API}/gallery/file/${it.id}`} alt={it.title || it.original_filename}
                      className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                  )}
                  <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase bg-zinc-950/80 text-cyan-400 px-2 py-0.5 rounded-full">
                    {it.media_type === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                    {it.media_type === "video" ? "Video" : "Foto"}
                  </span>
                  {it.title && (
                    <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-zinc-950 to-transparent p-3 text-left text-xs font-bold text-zinc-100 truncate">
                      {it.title}
                    </span>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/90 backdrop-blur-sm"
          onClick={() => setActive(null)} data-testid="gallery-lightbox">
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {active.media_type === "video" ? (
              <video src={`${API}/gallery/file/${active.id}`} controls autoPlay className="w-full max-h-[80vh] bg-black" />
            ) : (
              <img src={`${API}/gallery/file/${active.id}`} alt={active.title} className="w-full max-h-[80vh] object-contain" />
            )}
            {active.title && <p className="mt-3 text-center text-zinc-300 font-heading">{active.title}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
